import { ExcelExportParams, ProcessCellForExportParams } from '@ag-grid-community/core';
import { inject, Injectable } from '@angular/core';
import { combineLatest, map, tap } from 'rxjs';

import { SitesService } from '@models/sites/sites.service';

import {
  DocumentLibraryFile,
  DocumentLibraryService,
} from '@shared/services/document-library.service';
import {
  FetchDocumentsInput,
  GqlService,
  listDocumentsQuery,
  listOrganizationsQuery,
  listSitesQuery,
  listUserNamesWithEmailQuery,
  OrganizationType,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { Option } from '@shared/types/components.type';
import { Utils } from '@shared/utils/utils';

type ExtraDocumentData = [
  listOrganizationsQuery[],
  listUserNamesWithEmailQuery[],
  listSitesQuery[],
];

@Injectable({
  providedIn: 'root',
})
export class DocumentsPageService {
  authQuery = inject(AuthQuery);
  documentLibraryService = inject(DocumentLibraryService);
  gqlService = inject(GqlService);
  mainQuery = inject(MainQuery);
  overlayService = inject(OverlayService);
  sitesService = inject(SitesService);
  vendorsService = inject(OrganizationService);

  excelOptions = {
    author: 'Auxilius',
    columnWidth(params) {
      switch (params.column?.getId()) {
        case 'create_date':
          return 120;
        case 'description':
          return 180;
        case 'name':
          return 300;
        default:
          return 225;
      }
    },
    fileName: 'auxilius-document-library.xlsx',
    fontSize: 11,
    sheetName: 'Document Library',
    shouldRowBeSkipped(params) {
      return !params.node?.data?.id;
    },
  } as ExcelExportParams;
  sites: Option[] = [];
  users: listUserNamesWithEmailQuery[] = [];
  vendors: Option[] = [];

  getDocumentsWithPagination(paginationParams: FetchDocumentsInput) {
    return this.gqlService.listDocuments$(paginationParams).pipe(
      map<
        GraphqlResponse<listDocumentsQuery>,
        { data: DocumentLibraryFile[]; totalItems?: null | number }
      >(({ data, errors, success }) => {
        if (data?.items && success) {
          return {
            data: this.prepareDataToGrid(data.items),
            totalItems: data.meta_data?.total_count,
          };
        }

        this.overlayService.error(errors);

        return {
          data: [],
          totalItems: 0,
        };
      })
    );
  }

  getDynamicExcelParams = (): ExcelExportParams => {
    const name = this.mainQuery.getSelectedTrial()?.short_name;

    return {
      ...this.excelOptions,
      columnKeys: [
        'description',
        'name',
        'document_type_id',
        'vendor_id',
        'site_id',
        'create_date',
        'created_by',
      ],
      prependContent: [
        {
          cells: [
            {
              data: { type: 'String', value: `Trial: ${name}` },
              mergeAcross: 6,
              styleId: 'first_row',
            },
          ],
        },
      ],
      processCellCallback: (params: ProcessCellForExportParams): string => {
        const colId = params.column.getColId();
        const mapColFormatterById = new Map<string, () => string>([
          [
            'create_date',
            () =>
              Utils.awsDateFormatter(params.value, {
                day: '2-digit',
                hour: 'numeric',
                minute: 'numeric',
                month: '2-digit',
                year: 'numeric',
              }),
          ],
          [
            'created_by',
            () =>
              Utils.agUserFormatter(
                this.users.find(({ sub }) => sub === params.value) as listUserNamesWithEmailQuery,
                this.authQuery.isAuxAdmin()
              ),
          ],
          [
            'document_type_id',
            () =>
              Utils.getOptionLabel(this.documentLibraryService.getDocumentOptions(), params.value),
          ],
          ['site_id', () => Utils.getOptionLabel(this.sites, params.value)],
          ['vendor_id', () => Utils.getOptionLabel(this.vendors, params.value)],
        ]);

        const getFormatterCol = mapColFormatterById.get(colId);

        return getFormatterCol ? getFormatterCol() : params.value;
      },
    } as ExcelExportParams;
  };

  getRequiredDictionaries() {
    return combineLatest([
      this.vendorsService.get(),
      this.mainQuery.select('userList'),
      this.sitesService.get(),
    ]).pipe(
      map((responses) => {
        return responses.map((res) => ('data' in res ? res.data || [] : res)) as ExtraDocumentData;
      }),
      tap(([vendors, users, sites]: ExtraDocumentData) => {
        this.users = users;
        this.vendors = this.sortOptions(
          vendors
            .filter((vendor) => vendor.organization_type === OrganizationType.ORGANIZATION_VENDOR)
            .map(({ id, name }) => ({
              label: name,
              value: id,
            }))
        );
        this.sites = this.sortOptions(
          sites.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        );
      })
    );
  }

  prepareDataToGrid(data: listDocumentsQuery['items']) {
    return data.map((row) => {
      return {
        ...row,
        site_id: this.sites.find((option) => option.value === row.site_id)?.value || null,
        updatedByName: this.users.find(({ sub }) => sub === row.created_by) || null,
        vendor_id: this.vendors.find((option) => option.value === row.vendor_id)?.value || null,
      };
    }) as DocumentLibraryFile[];
  }

  sortOptions(options: Option[]) {
    return options.sort(({ label }, { label: label2 }) => Utils.localeAlphaNumSort(label, label2));
  }
}
